import { Button, Form, Modal, notification } from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createStudent } from "store/slices/studentsSlice";
import { closeModal } from "store/slices/modalsSlice";
import { getSchools } from "store/slices/schoolsSlice";
import StudentForm from "./student-form";
import { useTranslation } from "react-i18next";

const NewStudentModal = () => {
  const [form] = Form?.useForm();
  const dispatch = useDispatch();
  const { isModalOpen, modalType } = useSelector((state) => state?.modals);
  const { loading } = useSelector((state) => state?.students);
  const { schools } = useSelector((state) => state?.schools);
  const open = isModalOpen && modalType === "new_student";
  const { t } = useTranslation();
  const [isMinor, setIsMinor] = useState(false);

  const { userData } = useSelector((state) => state?.auth);

  useEffect(() => {
    if (open) {
      if (userData?._id) dispatch(getSchools(userData?._id));
      form?.resetFields();
    }
  }, [dispatch, open, userData?._id, form]);

  // Watch for changes in birth date to determine if parent info is required
  const handleFormValuesChange = (changedValues) => {
    if (changedValues?.birthDate) {
      const age = dayjs()?.diff(dayjs(changedValues?.birthDate), "years");
      setIsMinor(age < 18);

      // Update form validation rules if age indicates a minor
      if (age < 18) {
        form?.validateFields([
          "fatherFullName",
          "fatherPhoneNumber",
          "fatherOccupation",
          "fatherNationalId",
        ]);
      }
    }
  };

  const onSubmit = async (values) => {
    try {
      if (values?.birthDate) {
        const birthDate = dayjs(values?.birthDate)?.format("YYYY-MM-DD");
        values.birthDate = birthDate;

        // Check if student is a minor and requires parent info
        const age = dayjs()?.diff(dayjs(birthDate), "years");
        if (age < 18) {
          // Validate parent information for minors
          if (
            !values?.fatherFullName ||
            !values?.fatherPhoneNumber ||
            !values?.fatherOccupation ||
            !values?.fatherNationalId
          ) {
            notification?.error({
              message: "Validation Error",
              description: t("students.crud.form.error.parentInfo.required"),
            });
            return;
          }
        }
      }

      const result = await dispatch(createStudent(values))?.unwrap();

      notification?.success({
        message: "Success",
        description: t("students.crud.form.success"),
      });
      onClose();
    } catch (error) {
      console?.error("Error creating student:", error);
      notification?.error({
        message: "Error",
        description: error || t("students.crud.form.error.failed"),
      });
    }
  };

  const onClose = () => {
    form?.resetFields();
    setIsMinor(false);
    dispatch(closeModal());
  };

  // ...

  return (
    <Modal
      title={<h4 style={{ textAlign: "center" }}>{t("students.crud.new")}</h4>}
      open={open}
      onCancel={onClose}
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={onClose}>
          {t("students.form.button.cancel")}
        </Button>,
        <Button
          key="submit"
          type="primary"
          htmlType="submit"
          form="student_form"
          disabled={loading}
        >
          {t("students.form.button.save")}
        </Button>,
      ]}
      centered
      styles={{ content: { overflowY: "auto", maxHeight: "75vh" } }}
    >
      <StudentForm
        form={form}
        schools={schools}
        onSubmit={onSubmit}
        onValuesChange={handleFormValuesChange}
        isMinor={isMinor} // Ajout de la prop isMinor
      />
    </Modal>
  );
};

export default NewStudentModal;